import React, { useCallback, useEffect, useRef, useState } from "react";

// Admin-controlled difficulty (round.gamePlayLabel) maps to a grid size.
const LEVEL_SIZE = { easy: 9, medium: 14, hard: 20 };
function mazeSizeFor(label) {
  return LEVEL_SIZE[String(label || "").trim().toLowerCase()] || LEVEL_SIZE.medium;
}

const DIRS = [
  ["N", -1, 0],
  ["S", 1, 0],
  ["E", 0, 1],
  ["W", 0, -1],
];
const OPP = { N: "S", S: "N", E: "W", W: "E" };

/** Recursive-backtracker maze. Each cell stores which of its 4 walls remain. */
function generateMaze(size) {
  const cells = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => ({ N: true, E: true, S: true, W: true, visited: false }))
  );
  const stack = [[0, 0]];
  cells[0][0].visited = true;

  while (stack.length) {
    const [r, c] = stack[stack.length - 1];
    const open = DIRS.filter(([, dr, dc]) => {
      const nr = r + dr;
      const nc = c + dc;
      return nr >= 0 && nr < size && nc >= 0 && nc < size && !cells[nr][nc].visited;
    });
    if (open.length === 0) {
      stack.pop();
      continue;
    }
    const [dir, dr, dc] = open[Math.floor(Math.random() * open.length)];
    const nr = r + dr;
    const nc = c + dc;
    cells[r][c][dir] = false;
    cells[nr][nc][OPP[dir]] = false;
    cells[nr][nc].visited = true;
    stack.push([nr, nc]);
  }
  return cells;
}

const KEY_TO_DIR = {
  ArrowUp: "N",
  ArrowDown: "S",
  ArrowLeft: "W",
  ArrowRight: "E",
};

export default function MazeGame({ round, disabled, onSolved, buttonBg, buttonText }) {
  const [SIZE] = useState(() => mazeSizeFor(round.gamePlayLabel));
  const btnStyle = {
    ...(buttonBg && { background: buttonBg }),
    ...(buttonText && { color: buttonText }),
  };
  const [maze] = useState(() => generateMaze(SIZE));
  const [pos, setPos] = useState({ r: 0, c: 0 });
  const solvedRef = useRef(false);
  const posRef = useRef(pos);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const goal = { r: SIZE - 1, c: SIZE - 1 };

  const move = useCallback(
    (dir) => {
      if (disabled || solvedRef.current || !dir) return;
      const { r, c } = posRef.current;
      if (maze[r][c][dir]) return; // wall blocks this move
      const [, dr, dc] = DIRS.find(([d]) => d === dir);
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= SIZE || nc < 0 || nc >= SIZE) return;
      setPos({ r: nr, c: nc });
      if (nr === goal.r && nc === goal.c && !solvedRef.current) {
        solvedRef.current = true;
        onSolved({ answer: round.mazeExit || "solved", isCorrect: true });
      }
    },
    [disabled, maze, goal.r, goal.c, onSolved, round.mazeExit, SIZE]
  );

  useEffect(() => {
    const onKey = (e) => {
      const dir = KEY_TO_DIR[e.key];
      if (!dir) return;
      e.preventDefault();
      move(dir);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  // Carve a visible opening in the outer wall at the start (top-left) and the
  // goal (bottom-right) so the maze has a real entry and exit.
  const lines = [];
  for (let r = 0; r < SIZE; r += 1) {
    for (let c = 0; c < SIZE; c += 1) {
      const cell = maze[r][c];
      const isEntry = r === 0 && c === 0;
      const isExit = r === SIZE - 1 && c === SIZE - 1;
      if (cell.N && !isEntry) lines.push(`M${c} ${r} L${c + 1} ${r}`);
      if (cell.S && !isExit) lines.push(`M${c} ${r + 1} L${c + 1} ${r + 1}`);
      if (cell.W) lines.push(`M${c} ${r} L${c} ${r + 1}`);
      if (cell.E) lines.push(`M${c + 1} ${r} L${c + 1} ${r + 1}`);
    }
  }

  return (
    <div className="tg-maze">
      <div className="tg-maze-board">
        <svg viewBox={`-0.6 -1.1 ${SIZE + 1.2} ${SIZE + 2.2}`} className="tg-maze-svg">
          {/* Entry marker + label (Dawar) above the top-left opening */}
          {round.mazeEntry ? (
            <>
              <text x={0.5} y={-0.55} className="tg-maze-label" textAnchor="middle">
                {round.mazeEntry}
              </text>
              <path d="M0.2 -0.35 L0.8 -0.35 L0.5 0.05 Z" className="tg-maze-arrow-entry" />
            </>
          ) : null}

          <rect
            x={goal.c + 0.15}
            y={goal.r + 0.15}
            width={0.7}
            height={0.7}
            className="tg-maze-goal"
            rx={0.12}
          />
          <path d={lines.join(" ")} className="tg-maze-walls" fill="none" />

          {/* Player character */}
          <text
            x={pos.c + 0.5}
            y={pos.r + 0.5}
            className="tg-maze-player-char"
            textAnchor="middle"
            dominantBaseline="central"
          >
            🧗
          </text>

          {/* Exit marker + label (Razdan pass) below the bottom-right opening */}
          {round.mazeExit ? (
            <>
              <path
                d={`M${SIZE - 0.8} ${SIZE + 0.05} L${SIZE - 0.2} ${SIZE + 0.05} L${SIZE - 0.5} ${SIZE + 0.45} Z`}
                className="tg-maze-arrow-exit"
              />
              <text x={SIZE - 0.5} y={SIZE + 0.95} className="tg-maze-label" textAnchor="middle">
                {round.mazeExit}
              </text>
            </>
          ) : null}
        </svg>
      </div>

      <div className="tg-maze-controls">
        <button type="button" className="tg-maze-btn" style={btnStyle} onClick={() => move("N")} disabled={disabled} aria-label="Up">
          &#9650;
        </button>
        <div className="tg-maze-controls-row">
          <button type="button" className="tg-maze-btn" style={btnStyle} onClick={() => move("W")} disabled={disabled} aria-label="Left">
            &#9664;
          </button>
          <button type="button" className="tg-maze-btn" style={btnStyle} onClick={() => move("S")} disabled={disabled} aria-label="Down">
            &#9660;
          </button>
          <button type="button" className="tg-maze-btn" style={btnStyle} onClick={() => move("E")} disabled={disabled} aria-label="Right">
            &#9654;
          </button>
        </div>
      </div>
    </div>
  );
}
